import { ApiError } from '../client';
import type { PanelRole } from '../schemas/me';
import type {
  CatalogPermission,
  CatalogRole,
  RoleAssignment,
  RoleAssignmentsResponse,
  RoleAssignmentWrite,
} from '../schemas/roleAssignments';
import { RoleAssignmentsResponseSchema } from '../schemas/roleAssignments';

// The catalog as app-service delivered it: the two roles this screen grants, plus «Sistemas», listed as not
// assignable so the screen can compute effective permissions and recognise the rows it must leave
// alone. Producto grants both operations views, and both are restricted — door access and the
// carriers' exclusion lists, whose writes are not yet protected on the server.
const CATALOG_ROLES: readonly CatalogRole[] = [
  {
    id: 'cc',
    name: 'Customer Care',
    permissions: ['care.search'],
    assignable: true,
  },
  {
    id: 'product',
    name: 'Producto',
    permissions: ['operations.amazon_door_access', 'operations.carrier_exclusion_lists'],
    assignable: true,
  },
  {
    id: 'systems',
    name: 'Sistemas',
    permissions: ['access.manage'],
    assignable: false,
  },
];

// Sorted by id, as app-service lists them.
const CATALOG_PERMISSIONS: readonly CatalogPermission[] = [
  { id: 'access.manage', restricted: false },
  { id: 'care.search', restricted: false },
  { id: 'operations.amazon_door_access', restricted: true },
  { id: 'operations.carrier_exclusion_lists', restricted: true },
];

const CC: PanelRole = { id: 'cc', name: 'Customer Care' };
const PRODUCT: PanelRole = { id: 'product', name: 'Producto' };
const SYSTEMS: PanelRole = { id: 'systems', name: 'Sistemas' };
// Outside the catalog, so app-service names it by its id.
const MAINTENANCE: PanelRole = { id: 'maintenance', name: 'maintenance' };

// One row per case the screen has to draw. Fictional people only. `pablo` holds a role and an extra
// the catalog does not list, so the screen has something to show read-only and the write something
// to preserve; `sara` holds a per-person extra on top of her role; `marta` is «Sistemas», the row no
// write may touch.
const SEED: readonly RoleAssignment[] = [
  { email: 'ana@citibox.com', roles: [CC], extraPermissions: [], version: '1' },
  {
    email: 'luis@citibox.com',
    roles: [CC, PRODUCT],
    extraPermissions: [],
    version: '1',
  },
  {
    email: 'marta@citibox.com',
    roles: [SYSTEMS],
    extraPermissions: [],
    version: '1',
  },
  {
    email: 'pablo@citibox.com',
    roles: [MAINTENANCE],
    extraPermissions: ['maintenance.view'],
    version: '1',
  },
  {
    email: 'sara@citibox.com',
    roles: [CC],
    extraPermissions: ['operations.amazon_door_access'],
    version: '1',
  },
];

// Deep copies in and out: a caller that mutates what it read must not reach the store.
function copyOf(assignment: RoleAssignment): RoleAssignment {
  return {
    email: assignment.email,
    roles: assignment.roles.map((role) => ({ ...role })),
    extraPermissions: [...assignment.extraPermissions],
    version: assignment.version,
  };
}

let catalogRoles: readonly CatalogRole[] = CATALOG_ROLES;
let rows = new Map<string, RoleAssignment>();
let forcedReadError: string | null = null;
let forcedWriteError: string | null = null;

// A catalog and rows other than the seed, for a test that needs a case the seed does not draw —
// a non-assignable role that grants no access management, say.
export interface RoleAssignmentsMockState {
  catalogRoles?: readonly CatalogRole[];
  seed?: readonly RoleAssignment[];
}

// Exported for tests: module state outlives an `it`, so a suite that writes before it reads would
// otherwise depend on its own declaration order. Without a state it restores the seed.
export function resetRoleAssignmentsMock(state: RoleAssignmentsMockState = {}): void {
  catalogRoles = (state.catalogRoles ?? CATALOG_ROLES).map((role) => ({
    ...role,
    permissions: [...role.permissions],
  }));
  rows = new Map((state.seed ?? SEED).map((assignment) => [assignment.email, copyOf(assignment)]));
  forcedReadError = null;
  forcedWriteError = null;
}

resetRoleAssignmentsMock();

// Dev/test hook: fail the read with a chosen code, so the screen's load error and its «denied»
// state can be reached by hand under VITE_USE_MOCK_API.
export function setRoleAssignmentsReadMockError(code: string | null): void {
  forcedReadError = code;
}

// Dev/test hook for both writes. The mock has no caller identity, so refusing a person's own row
// (`assignment_self_edit`) and a write-time 403 are reachable only through here.
export function setRoleAssignmentsMockError(code: string | null): void {
  forcedWriteError = code;
}

// The status the DELIVERED contract pairs with each code (CBX-11681): the authentication codes are
// 401s, the two access refusals 403s. The `assignment_*` business rules the server refuses are 422s,
// and an injected code the contract does not list falls there too.
function statusFor(code: string): number {
  if (code === 'not_authenticated' || code === 'authentication_failed') {
    return 401;
  }
  if (code === 'permission_denied' || code === 'access_manage_required') {
    return 403;
  }
  if (code === 'assignment_version_conflict') {
    return 409;
  }
  if (code === 'assignment_email_invalid' || code === 'invalid_data') {
    return 400;
  }
  return 422;
}

// Every write refusal carries the `{code}` envelope the real resource answers with, so
// the panel's error mapping reads the same field in mock mode as against app-service.
function refuse(code: string): ApiError {
  return new ApiError(statusFor(code), code, { code }, `role assignment write failed: ${code}`);
}

// A row with no roles and no extras is a bare version header: the person has nothing, but the
// version «Quitar todo» left behind is kept so a write read before the removal stays stale.
function holdsNothing(assignment: RoleAssignment | undefined): boolean {
  return (
    assignment === undefined ||
    (assignment.roles.length === 0 && assignment.extraPermissions.length === 0)
  );
}

// The catalog plus the stored rows sorted by email, as app-service lists them, in one response;
// a bare header is not listed. Not copied: the read parses it, and a parse builds new objects, so
// nothing a caller mutates reaches the store.
function currentState(): RoleAssignmentsResponse {
  return {
    roles: [...catalogRoles],
    permissions: [...CATALOG_PERMISSIONS],
    assignments: [...rows.values()]
      .filter((assignment) => !holdsNothing(assignment))
      .sort((a, b) => (a.email < b.email ? -1 : a.email > b.email ? 1 : 0)),
  };
}

// The store as app-service would send it, so the mock read goes through the same parse as the real
// one and cannot drift from the delivered shape.
function toWire(state: RoleAssignmentsResponse): unknown {
  return {
    roles: state.roles,
    permissions: state.permissions,
    assignments: state.assignments.map(({ email, roles, extraPermissions, version }) => ({
      email,
      roles,
      extra_permissions: extraPermissions,
      version,
    })),
  };
}

// Exported for tests: the wire payload the mock read parses.
export function roleAssignmentsMockWire(): unknown {
  return toWire(currentState());
}

export async function fetchRoleAssignmentsMock(): Promise<RoleAssignmentsResponse> {
  await new Promise((resolve) => setTimeout(resolve, 100));
  if (forcedReadError !== null) {
    throw new ApiError(
      statusFor(forcedReadError),
      forcedReadError,
      { code: forcedReadError },
      `role assignments read failed: ${forcedReadError}`,
    );
  }
  return RoleAssignmentsResponseSchema.parse(roleAssignmentsMockWire());
}

function catalogRole(roleId: string): CatalogRole | undefined {
  return catalogRoles.find((role) => role.id === roleId);
}

// The canonical email the server accepts: printable ASCII with no spaces, exactly one `@`, a
// non-empty local part without a `+` alias, the citibox domain.
const CITIBOX_EMAIL = /^[\x21-\x2a\x2c-\x3f\x5b-\x7e]+@citibox\.com$/;

function isAccessManagement(permissionId: string): boolean {
  return permissionId.startsWith('access.');
}

// Extras this screen may grant: catalog permissions, never an access-management one.
const GRANTABLE_EXTRAS = new Set(
  CATALOG_PERMISSIONS.map((permission) => permission.id).filter((id) => !isAccessManagement(id)),
);

function grantsAccessManagement(roleId: string): boolean {
  return catalogRole(roleId)?.permissions.some(isAccessManagement) ?? false;
}

// A catalog role this screen may not grant: one listed as not assignable, or one granting access
// management.
function isProtectedRole(roleId: string): boolean {
  const role = catalogRole(roleId);
  return role !== undefined && (!role.assignable || grantsAccessManagement(roleId));
}

// Effective permissions are what the row's roles grant through the catalog plus its extras; a role
// the catalog does not list grants nothing the store can see.
function holdsAccessManagement(assignment: RoleAssignment): boolean {
  return (
    assignment.extraPermissions.some(isAccessManagement) ||
    assignment.roles.some((role) => grantsAccessManagement(role.id))
  );
}

// The server canonicalises the email it receives, and the store is keyed by that form,
// so «Ana@Citibox.com » and «ana@citibox.com» are one person.
function canonical(email: string): string {
  return email.trim().toLowerCase();
}

// The mock's own counter, continued from a kept header. The real version is opaque; only this
// store knows it is a number.
function nextVersion(current: RoleAssignment | undefined): string {
  return current === undefined ? '1' : String(Number(current.version) + 1);
}

// The guards run in the order the server runs them, and the store moves only after all of them
// pass, so a refused write changes nothing: an injected failure, the email, the shape of the write,
// the requested roles, the protection of the row, the extras, and the compare-and-set last — a
// refused write says why, not «somebody else changed it».
export async function upsertRoleAssignmentMock(write: RoleAssignmentWrite): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 100));
  if (forcedWriteError !== null) {
    throw refuse(forcedWriteError);
  }
  const email = canonical(write.email);
  if (!CITIBOX_EMAIL.test(email)) {
    throw refuse('assignment_email_invalid');
  }
  // Taking someone's access away is «Quitar todo», never an empty list.
  if (write.roles.length === 0 && write.extraPermissions.length === 0) {
    throw refuse('invalid_data');
  }
  if (write.roles.some(isProtectedRole)) {
    throw refuse('assignment_systems_protected');
  }
  if (write.roles.some((id) => catalogRole(id) === undefined)) {
    throw refuse('assignment_permission_not_assignable');
  }
  const current = rows.get(email);
  if (current !== undefined && holdsAccessManagement(current)) {
    throw refuse('assignment_systems_protected');
  }
  // The extras are the whole set, so one outside the grantable catalog is accepted only when the
  // row already holds it: the client is sending back what it showed read-only.
  const stored = new Set(current?.extraPermissions ?? []);
  if (
    write.extraPermissions.some(
      (id) => isAccessManagement(id) || (!GRANTABLE_EXTRAS.has(id) && !stored.has(id)),
    )
  ) {
    throw refuse('assignment_permission_not_assignable');
  }
  // The compare-and-set. `null` is a claim like any other — «this person has nothing» — so it is
  // compared, never read as «skip the check». A bare header counts as nothing.
  const stale =
    write.expectedVersion === null
      ? !holdsNothing(current)
      : holdsNothing(current) || current?.version !== write.expectedVersion;
  if (stale) {
    throw refuse('assignment_version_conflict');
  }
  // The catalog roles are replaced by the ones sent — in catalog order, once each, named by the
  // catalog, since the write carries ids only. A role the catalog does not list is kept: the client
  // never saw it as its own to change.
  const managed = catalogRoles
    .filter((role) => write.roles.includes(role.id))
    .map(({ id, name }) => ({ id, name }));
  const unmanaged = (current?.roles ?? [])
    .filter((role) => catalogRole(role.id) === undefined)
    .map((role) => ({ ...role }));
  rows.set(email, {
    email,
    roles: [...managed, ...unmanaged],
    extraPermissions: [...new Set(write.extraPermissions)],
    version: nextVersion(current),
  });
}

// «Quitar todo»: empties the whole row, unmanaged roles and extras included — the one write that
// takes what the client does not manage. The version header is kept and bumped, never deleted. As
// on the server, a protected row is refused before the version is compared; a person with nothing
// or a stale version is a 409.
export async function removeAllRoleAssignmentMock(
  email: string,
  expectedVersion: string,
): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 100));
  if (forcedWriteError !== null) {
    throw refuse(forcedWriteError);
  }
  const key = canonical(email);
  const current = rows.get(key);
  if (current !== undefined && holdsAccessManagement(current)) {
    throw refuse('assignment_systems_protected');
  }
  if (current === undefined || holdsNothing(current) || current.version !== expectedVersion) {
    throw refuse('assignment_version_conflict');
  }
  rows.set(key, { email: key, roles: [], extraPermissions: [], version: nextVersion(current) });
}
